import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { CalendarCheck, Plus, Sparkle } from "@phosphor-icons/react";
import { StatePill, fmtD } from "../ppm/ppmUtils";
import { PpmScheduleDialog } from "../ppm/PpmScheduleDialog";

/** Asset detail — PPM schedules for this asset, plus one-click creation from its manual's service tasks. */
export function AssetPpmCard({ asset, canEdit }) {
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [options, setOptions] = useState(null);
  const [library, setLibrary] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { API.get("/ppm/schedules", { params: { asset_id: asset.id } }).then((r) => setRows(r.data.schedules)).catch(() => setRows([])); }, [asset.id]);
  useEffect(() => { load(); }, [load]);
  const openNew = async () => {
    if (!options) { const [o, l] = await Promise.all([API.get("/ppm/options"), API.get("/ppm/library")]); setOptions(o.data); setLibrary(l.data); }
    setDlg(true);
  };
  const fromAsset = async () => {
    setBusy(true);
    try { const { data } = await API.post(`/ppm/schedules/from-asset/${asset.id}`, {}); toast.success(`${data.count} PPM schedule${data.count === 1 ? "" : "s"} created from the service plan`); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't create schedules"); } finally { setBusy(false); }
  };
  const hasPlan = (asset.service_tasks || []).length > 0 || asset.service_interval_days > 0;
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="asset-ppm-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold flex items-center gap-1.5"><CalendarCheck size={16} className="text-primary" /> Planned maintenance</h3>
        <div className="flex gap-1">
          {canEdit && hasPlan && !rows?.length && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={fromAsset} data-testid="asset-ppm-from-plan"><Sparkle size={12} className="mr-1" /> From service plan</Button>}
          {canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openNew} data-testid="asset-ppm-add"><Plus size={12} className="mr-1" /> Add</Button>}
        </div>
      </div>
      {!rows ? <div className="h-10 animate-pulse rounded bg-muted/40" /> : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="asset-ppm-empty">No PPM schedules yet{hasPlan ? " — create them from the manual's service plan in one click." : "."}</p>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`asset-ppm-row-${r.id}`}>
              <div className="min-w-0 flex-1"><p className="font-medium truncate">{r.title}</p><p className="text-[10px] text-muted-foreground">{r.frequency_label} · next {fmtD(r.next_due)}{r.operative_names?.length ? ` · ${r.operative_names.join(", ")}` : r.vendor_name ? ` · ${r.vendor_name}` : ""}</p></div>
              <StatePill state={r.state} />
              {r.open_job && <Link to={`/jobs/${r.open_job.id}`} className="text-primary font-medium whitespace-nowrap">{r.open_job.reference}</Link>}
            </div>
          ))}
          <Link to={`/ppm?tab=schedules`} className="block pt-2 text-[11px] text-primary" data-testid="asset-ppm-open-planner">Open the PPM planner →</Link>
        </div>
      )}
      <PpmScheduleDialog open={dlg} schedule={null} preset={{ asset_id: asset.id, property_id: asset.property_id || "", basis: "floating" }} options={options} library={library} onClose={() => setDlg(false)} onSaved={load} />
    </div>
  );
}
